import { forwardRef, ReactNode, useEffect, useImperativeHandle, useState } from 'react';
import { FileRejection } from 'react-dropzone';

import { getFilesData, type FileData } from '../utils/get-files-data';
import { PreviewDetail } from './preview-detail';
import { PreviewPopover } from './preview-popover';

export type PreviewRef = {
  getAcceptedFiles: () => { acceptedFiles: FileData[] };
  getRejectedFiles: () => { rejectedFiles: FileData[] };
};

interface PreviewProps {
  acceptedFiles: File[];
  disabled?: boolean;
  popoverWarningDetail?: ReactNode;
  rejectedFiles: FileRejection[];
  sizeAlert?: {
    none: number;
    medium: number;
  };
}

export const Preview = forwardRef<PreviewRef, PreviewProps>(function Preview(
  { acceptedFiles, disabled = false, rejectedFiles, sizeAlert, popoverWarningDetail },
  ref,
) {
  const [acceptFiles, setAcceptFiles] = useState<FileData[]>([]);
  const [rejectFiles, setRejectFiles] = useState<FileData[]>([]);

  useEffect(() => {
    const updateAcceptFiles = async () => {
      const filesData = await getFilesData(acceptedFiles);
      setAcceptFiles(filesData);
    };
    updateAcceptFiles();
  }, [acceptedFiles]);

  useEffect(() => {
    const updateRejectFiles = async () => {
      const filesData = await getFilesData(rejectedFiles);
      setRejectFiles(filesData);
    };
    updateRejectFiles();
  }, [rejectedFiles]);

  useImperativeHandle(ref, () => ({
    getAcceptedFiles() {
      return { acceptedFiles: acceptFiles };
    },
    getRejectedFiles() {
      return { rejectedFiles: rejectFiles };
    },
  }));

  return (
    <div>
      <PreviewDetail
        acceptedFiles={acceptFiles}
        disabled={disabled}
        sizeAlert={sizeAlert}
        popoverWarningDetail={popoverWarningDetail}
      />
      <div className="flex min-h-20 flex-wrap items-center gap-3 rounded-lg border bg-muted/20 p-3">
        {acceptFiles.map((acceptFile) => (
          <PreviewPopover
            key={acceptFile.file.name}
            disabled={disabled}
            fileData={acceptFile}
            sizeAlert={sizeAlert}
            onRemove={({ fileData }) => {
              // removes the selected file
              const updatedAcceptFiles = acceptFiles.filter(
                (item) => item.file.name !== fileData.file.name,
              );
              setAcceptFiles(updatedAcceptFiles);
            }}
            onSubmit={({ fileData, name }) => {
              // updates the name of the selected file
              let fileDataChanged = false;
              const updatedAcceptFiles = acceptFiles.map((item) => {
                if (item.file.name === fileData.file.name) {
                  fileDataChanged = true;
                  return { ...fileData, name };
                }
                return item;
              });
              if (fileDataChanged) {
                setAcceptFiles(updatedAcceptFiles);
              }
            }}
          />
        ))}
      </div>
    </div>
  );
});
